import { createClient } from 'graphql-ws';
import { DocumentNode } from 'graphql/index';
import { print } from 'graphql/language/printer';
import { Callback } from '@/types/utility';
import { AuthService } from '@/services/auth.service';

type ConnectionKey = string;
type Subscription = {
  unsubscribe: Callback;
  isConnected: Promise<void>;
};
const connectionPool = new Map<ConnectionKey, ReturnType<typeof createClient>>();

const getConnectionKey = (token?: string, objectId?: number) =>
  token && objectId ? `${token}_${objectId}` : 'default';

const getWsClient = (token?: string, objectId?: number) => {
  if (token == null && !AuthService.isAuthenticated()) {
    throw new Error('Not authenticated and no share token provided');
  }

  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const host = window.location.host;
  const url = `${protocol}://${host}/hasura/v1/graphql`;

  const connectionKey = getConnectionKey(token, objectId);

  if (connectionPool.has(connectionKey)) {
    return connectionPool.get(connectionKey)!;
  }

  const client = createClient({
    url,
    lazy: false,
    lazyCloseTimeout: 5000,
    retryAttempts: 10,
    connectionAckWaitTimeout: 5000,
    onNonLazyError(error) {
      console.error('onNonLazyError:', error);
    },
    async connectionParams() {
      const _token =
        token && objectId
          ? await AuthService.getHasuraAnonymousToken(token, String(objectId))
          : await AuthService.tokenRefreshedIfOld();

      return {
        headers: {
          'Hasura-Client-Name': 'hasura-console',
          'content-type': 'application/json',
          Authorization: `Bearer ${_token}`,
        },
      };
    },
  });

  connectionPool.set(connectionKey, client);
  return client;
};

export const subscribe = (
  subscriptionDocument: DocumentNode,
  variables: Record<string, unknown>,
  onData: Callback,
  shareToken?: string,
  sharedObjectId?: number,
): Subscription => {
  const client = getWsClient(shareToken, sharedObjectId);

  const query = print(subscriptionDocument);
  if (!query) {
    throw new Error('GraphQL file is invalid!');
  }

  const { promise, resolve, reject } = Promise.withResolvers<void>();

  const subscription = {
    unsubscribe: client.subscribe(
      {
        query,
        variables,
      },
      {
        next(data) {
          onData(data.data);
          resolve();
        },
        error(e) {
          reject(e);
          console.error(e);
        },
        complete() {
          // TODO: handle completion
        },
      },
    ),
  };

  return {
    async unsubscribe() {
      await this.isConnected;
      subscription.unsubscribe();
    },
    isConnected: promise,
  };
};
